import { Alert, Box, Group, IconWarning, Typography, Wrapper } from '@screentone/core';
import type { InfiniteData } from '@tanstack/react-query';

import type { AllessehSummariesResponse } from 'hooks/useAllessehCollectionSummariesQuery';
import type { AllessehContentQueryResponse } from 'hooks/useAllessehContentQuery';
import ArticleCard from './ArticleCard';
import ParametersDisplay from './ParametersDisplay';
import QueryDisplay from './QueryDisplay';
import { type Article, type CollectionOrArticle, findEmbargoArticleById, paramsArrayToObject } from '../Debug.utils';

interface CollectionNodeProps {
  item: CollectionOrArticle;
  index: number;
  level: number;
  rawNode: Article | null;
  collectionSummaryData: AllessehSummariesResponse;
  renderFromResolvedData: (collection: CollectionOrArticle[], level: number) => JSX.Element[] | null;
  missingArticles: Set<string>;
  embargoArticlesData?: InfiniteData<AllessehContentQueryResponse>;
  containerHeight: number;
}

const CollectionNode = ({
  item,
  index,
  level,
  rawNode,
  collectionSummaryData,
  renderFromResolvedData,
  missingArticles,
  embargoArticlesData,
  containerHeight
}: CollectionNodeProps) => {
  // Get parameters from resolved data
  const params = paramsArrayToObject(item.parameters as Array<{ type: string; name: string; value: unknown }>);

  // Get raw parameters if raw node exists
  const rawParams = rawNode
    ? paramsArrayToObject(rawNode.parameters as Array<{ type: string; name: string; value: unknown }>)
    : {};

  // Get articles for this collection (filter out nested collections)
  const articles =
    item.collection && Array.isArray(item.collection)
      ? item.collection.filter((child: CollectionOrArticle) => child.type !== 'collection')
      : [];

  // Get nested collections
  const nestedCollections =
    item.collection && Array.isArray(item.collection)
      ? item.collection.filter((child: CollectionOrArticle) => child.type === 'collection')
      : [];

  // Check if query returned different number of articles than requested
  const queryCountMismatch = (() => {
    if (!rawParams.query || typeof rawParams.query !== 'object') return null;

    const query = rawParams.query as { count?: number };
    const requestedCount = query.count;

    if (typeof requestedCount !== 'number') return null;

    const actualCount = articles.length;

    if (actualCount !== requestedCount) {
      return {
        requested: requestedCount,
        actual: actualCount,
        difference: Math.abs(requestedCount - actualCount),
        type: actualCount < requestedCount ? 'missing' : 'extra'
      };
    }

    return null;
  })();

  const nodeStyles: React.CSSProperties = {};
  if (params.collection_type === 'Curation_Module') {
    nodeStyles.position = 'sticky';
    nodeStyles.top = containerHeight + 40; // 40px from subnav
    nodeStyles.backgroundColor = 'var(--st-content-bg)';
  }

  // Check if this is a curation module without a module_id
  const isCurationModuleWithoutModuleId = params.collection_type === 'Curation_Module' && !params.module_id;

  return (
    <Wrapper
      key={`${item.id}-${index}`}
      margin={{ top: 'none', bottom: 'lg', right: 'md', left: level > 0 ? 'md' : undefined }}
    >
      {/* Collection Header */}
      <Typography variant="header4" style={nodeStyles}>
        {params.collection_type || 'Node'}: {params.module_id || params.group_id || `[${index}]`}
        {params.title && typeof params.title === 'string' && ` - ${params.title}`}
      </Typography>

      {/* Display warning if curation module is missing module_id */}
      {isCurationModuleWithoutModuleId && (
        <Alert type="warning" icon={IconWarning} inline>
          This Curation_Module node does not have a module_id set.
        </Alert>
      )}

      {/* Display error if query returned different number of articles than requested */}
      {queryCountMismatch && (
        <Alert type="warning" margin={{ bottom: 'md' }}>
          <strong>⚠️ Query Count Mismatch:</strong> Query requested {queryCountMismatch.requested} articles but{' '}
          {queryCountMismatch.actual} were returned ({queryCountMismatch.difference} {queryCountMismatch.type})
        </Alert>
      )}

      {/* Display basic properties */}
      {Object.entries(item)
        .filter(([key]) => key !== 'parameters' && key !== 'collection')
        .map(([key, value]) => (
          <Typography key={key} size="sm">
            <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </Typography>
        ))}

      {/* Display all parameters from raw data (preferred as it's more complete) */}
      {rawNode?.parameters && Array.isArray(rawNode.parameters) && (
        <ParametersDisplay parameters={rawNode.parameters} title="Parameters:" />
      )}

      {/* Display query if present in raw data */}
      {rawParams.query && typeof rawParams.query === 'object' && (
        <QueryDisplay query={rawParams.query as Record<string, unknown>} />
      )}

      {/* Display queries from child collections in raw data (only for non-feed collections) */}
      {rawNode?.collection &&
        Array.isArray(rawNode.collection) &&
        rawNode.collection.map((childNode: Article, childIdx: number) => {
          if (childNode.type !== 'collection' || !childNode.parameters) return null;

          const childParams = paramsArrayToObject(
            childNode.parameters as Array<{ type: string; name: string; value: unknown }>
          );

          // Skip if this child has feed_title/feed_url - it will render its own query
          if (childParams.feed_title || childParams.feed_url) return null;

          if (!childParams.query || typeof childParams.query !== 'object') return null;

          return (
            <QueryDisplay
              key={childIdx}
              query={childParams.query as Record<string, unknown>}
              label={`Query (from child ${childNode.id})`}
            />
          );
        })}

      {(rawParams.feed_title || rawParams.feed_url) && (
        <Group direction="column" gap="xs" margin={{ bottom: 'md' }}>
          <Typography size="sm">
            <strong>Feed:</strong> {rawParams.feed_title || 'Untitled Feed'}
          </Typography>
          <Typography size="sm">
            <strong>URL:</strong> {rawParams.feed_url || 'No URL provided'}
          </Typography>
        </Group>
      )}

      {/* Display articles */}
      {articles.length > 0 && (
        <Wrapper fullWidth>
          <Typography variant="label3">
            <strong>Articles ({articles.length}):</strong>
          </Typography>
          <Group direction="column" gap="xs" fullWidth align="stretch">
            {articles.map((article: CollectionOrArticle, articleIndex: number) => (
              <ArticleCard
                key={article.id}
                articleId={article.id}
                rawArticleData={article as Article}
                collectionSummaryData={collectionSummaryData}
                embargoArticleData={findEmbargoArticleById(article.id, embargoArticlesData)}
                isMissing={missingArticles.has(article.id)}
                index={articleIndex}
              />
            ))}
          </Group>
        </Wrapper>
      )}

      {/* Recursively render nested collections */}
      {nestedCollections.length > 0 && (
        <Box margin={{ top: 'sm' }} padding={{ top: 'sm' }}>
          {renderFromResolvedData(nestedCollections, level + 1)}
        </Box>
      )}
    </Wrapper>
  );
};

export default CollectionNode;
